/**
 * deals.js — client wrapper for the dealsApi callable (RW-827 P4.1 / RW-843).
 *
 * The TC deal & date model lives in the relational core (Postgres) behind a
 * server-authoritative domain API — there is NO client data path (RW-823).
 * Components/hooks call these methods and re-render from the responses;
 * realtime comes from the signals/{tenant}__deals Firestore signal doc
 * (see useDeals / useDeal), never from a direct DB connection.
 *
 * Sibling of src/services/offboarding.js (the RW-826 idiom). The callable
 * enforces every permission (staff-view / TC-manage / broker-approve) and RLS
 * tenant isolation server-side — these methods are a thin transport, not a
 * gate, and intentionally do not try/catch: callers (the hooks below) own
 * loading + toast.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

async function call(action, payload) {
  const callable = httpsCallable(functions, 'dealsApi');
  const result = await callable({ action, ...(payload || {}) });
  return result?.data;
}

export const dealsService = {
  // ── reads (staff) ─────────────────────────────────────────
  // RW-1510: { includeInactive: true } adds inactive templates (editor view).
  templates: (opts) => call('templates', opts),
  // RW-1510 editor: template + ordered tasks / apply edits (write is
  // broker/admin-gated server-side; simple fields only — engine metadata
  // like date rules and gates stays seed-managed).
  templateDetail: (templateId) => call('templateDetail', { templateId }),
  templateSave: (ops) => call('templateSave', ops),
  // activeOnly defaults to true server-side (activeOnly !== false); pass false for all.
  // RW-2942: closedFrom/closedTo ('YYYY-MM-DD') window the closed + lost deals;
  // includeOpen:false drops the open ones; ids = exactly those deals; q + limit
  // = search (newest first). Undefined keys are left off — never sent as null.
  list: ({ activeOnly, tcUid, agentUid, closedFrom, closedTo, includeOpen, ids, q, limit } = {}) => call('list', Object.fromEntries(
    Object.entries({ activeOnly, tcUid, agentUid, closedFrom, closedTo, includeOpen, ids, q, limit }).filter(([, v]) => v !== undefined),
  )),
  // RW-2942: People › Past clients — one row per person (imported history
  // included), paged: { clients, nextCursor, total }. Agents get their own
  // deals server-side; only staff's agentUid is honored. Absent keys stay off
  // the wire (the callable SDK sends undefined as null).
  pastClients: ({ q, limit, cursor, agentUid } = {}) => call('pastClients', {
    ...(q ? { q } : {}), ...(limit ? { limit } : {}), ...(cursor ? { cursor } : {}), ...(agentUid ? { agentUid } : {}),
  }),
  get: (dealId) => call('get', { dealId }),
  // Cross-deal overdue tasks in one query — the date-engine's headline win.
  overdue: ({ tcUid, agentUid } = {}) => call('overdue', { tcUid, agentUid }),
  // RW-1079: the personal cross-deal work list (always self-scoped server-side).
  myTasks: () => call('myTasks'),
  // RW-2867: the one task inbox — myTasks' deal rows plus the caller's
  // onboarding tasks → { tasks, onboarding, failed: ['deals'|'onboarding'] }.
  taskInbox: () => call('taskInbox'),
  /** RW-1534: clone a deal into a fresh file (dead files stay dead). Agents
   *  may copy their own deals; staff any. opts.stage = pre-contract start. */
  copyDeal: (dealId, opts = {}) => call('copyDeal', { dealId, ...opts }),
  /** RW-2852: from a deal that fell through, the next offer's NEW file (linked
   *  both ways, at Active, that side's people + documents). copyDeal refuses a
   *  lost deal now. Returns { dealId, existing? } — existing = it was started already. */
  newOffer: (dealId) => call('newOffer', { dealId }),
  // RW-1509: tenant-wide per-agent aggregates for the roster — any signed-in
  // caller; aggregates only ({ agentUid, count, volume }), never deal rows.
  rosterStats: () => call('rosterStats'),
  // RW-1646/1648: `closedProduction` was here. The board and the Dashboard rank
  // widget now read reportingService.leaderboardRanking — the Darwin book, where
  // the closings actually are. See src/services/reporting.js.
  /** RW-1522: tenant-wide leadership aggregates (broker/admin-gated server-side).
   *  RW-3862: dealClass 'sales' | 'leases' | 'fees' (RW-3873) slices them; 'all'
   *  or absent sends no key at all (the server default — never a null on the wire). */
  leadershipRollup: ({ dealClass } = {}) => call('leadershipRollup',
    dealClass === 'sales' || dealClass === 'leases' || dealClass === 'fees' ? { dealClass } : undefined),

  // ── mutations (tc / agent_services / elevated) ────────────
  // payload: { side, dealType, ...anchors, parties?, templateKey?,
  //   comingSoon?, buyerNeed? | linkBuyerId? } — validated server-side.
  // RW-1089: comingSoon births + publishes the linked app.listings row;
  // buyerNeed births the linked app.buyers row (or linkBuyerId links an
  // existing one) — all in the deal's own transaction, one record from birth.
  createDeal: (payload) => call('createDeal', payload),
  // patch carries anchor dates; the server cascade-recomputes derived due dates.
  updateDeal: (dealId, patch, extra = {}) => call('updateDeal', { dealId, patch, ...extra }), // RW-3230: extra.withDeal
  // RW-2821: the Deal facts card — staff or the deal's own agent; the seven facts only.
  // RW-3177: a closed deal's facts need { unlockReason } (after unlockDealFacts)
  updateDealFacts: (dealId, patch, extra = {}) => call('updateDealFacts', { dealId, patch, ...extra }),
  unlockDealFacts: (dealId, reason) => call('unlockDealFacts', { dealId, reason }),
  // RW-3059: update the deal file from a contract's AI check, or keep it —
  // fields only; the values are the ones the check stored
  applyContractTerms: (dealId, docId, version, { accept = [], keep = [] } = {}) =>
    call('applyContractTerms', { dealId, docId, version, accept, keep }),
  // RW-2869: tasks up to their checklist template's current version (staff) —
  // the given ones, or every one plus the template's current conditions when
  // taskIds is omitted (the key is left off, never sent as null).
  applyTemplateDrift: (dealId, taskIds) => call('applyTemplateDrift', { dealId, ...(taskIds ? { taskIds } : {}) }),
  // RW-916: move lifecycle (stage and/or terminal outcome) → cascades to the
  // linked listing/buyer/contact + notifies the team. Pipeline drag passes stage.
  changeStatus: (dealId, { stage, outcome } = {}) => call('changeStatus', { dealId, stage, outcome }),
  // RW-2850: fell through / cancelled / expired, with the coded reason (+ note).
  markLost: (dealId, { outcome, reason, note }) => call('changeStatus', { dealId, outcome, reason, ...(note ? { note } : {}) }),
  // RW-2844: a broker lets a dual deal go under contract without the signed consent (reason kept).
  overrideDualConsent: (dealId, reason) => call('overrideDualConsent', { dealId, reason }),
  // RW-2844: broker review — dual deals under contract with no consent and no override.
  dualConsentMissing: () => call('dualConsentMissing'),
  setParties: (dealId, parties) => call('setParties', { dealId, parties }),
  // RW-2847: in-house deals (two Rêve agents, one deal each, linked). The
  // other side's agent when they're at Rêve too (null clears it); accept a
  // colleague's deal that named you; end the pair.
  setInHouseAgent: (dealId, agentUid) => call('setInHouseAgent', { dealId, agentUid: agentUid || null }),
  linkInHouse: (dealId, counterpartDealId) => call('linkInHouse', { dealId, counterpartDealId }),
  unlinkInHouse: (dealId) => call('unlinkInHouse', { dealId }),
  dealEvents: (dealId, limit) => call('dealEvents', { dealId, ...(limit ? { limit } : {}) }),
  instantiate: (dealId, templateKey) => call('instantiate', { dealId, templateKey }),
  // RW-2870: what instantiate would create — the "Apply checklist" preview.
  previewChecklist: (dealId, templateKey) => call('previewChecklist', { dealId, templateKey }),
  // open | done | skipped. RW-2865: an agent's tick on a required task comes
  // back as { status: 'needs_review' } — it waits for the TC to check it.
  // RW-2868: a skip ("Not applicable") may carry a note — omitted when empty
  // (the callable SDK sends an undefined key as null).
  // RW-3157: `reason` — a super admin's override of a broker-approval step,
  // or why a compliance / closing review step is set aside (logged)
  setTaskStatus: (taskId, status, note, reason) => call('setTaskStatus', {
    taskId, status, ...(note ? { note } : {}), ...(reason ? { reason } : {}),
  }),
  // RW-2865: the TC checks an agent's ticks — confirm (any number), or send
  // one back with a note the agent sees.
  confirmTasks: (taskIds) => call('reviewTasks', { decision: 'confirm', taskIds }),
  sendTaskBack: (taskId, note) => call('reviewTasks', { decision: 'return', taskIds: [taskId], note }),
  // RW-2868 (T2): the TC's bulk actions on one deal — op 'done' | 'skipped' |
  // 'assign'. The note rides only with a non-empty skip; assign always says
  // who (null = back to the task's role); no other key is sent (the callable
  // SDK sends an undefined key as null).
  bulkTasks: (dealId, taskIds, op, { note, assigneeUid } = {}) => call('bulkTasks', {
    dealId, taskIds, op,
    ...(op === 'skipped' && note ? { note } : {}),
    ...(op === 'assign' ? { assigneeUid: assigneeUid || null } : {}),
  }),
  // RW-2864: remove an ad-hoc task (staff, or the agent who added it).
  removeTask: (taskId) => call('removeTask', { taskId }),
  // RW-1085: ad-hoc action item on the deal's checklist instance (manage-gated;
  // fails precondition until the deal's checklist exists).
  addTask: (dealId, { title, assigneeRole, assigneeUid, dueDate } = {}) => call('addTask', { dealId, title, assigneeRole, assigneeUid, dueDate }),
  // RW-1511: set/clear a task's person assignment (uid wins over role;
  // null falls the task back to its role).
  assignTask: (taskId, assigneeUid) => call('assignTask', { taskId, assigneeUid: assigneeUid || null }),
  // RW-1514: self-serve watch/unwatch (notify-only; never changes ownership).
  watchTask: (taskId, on) => call('watchTask', { taskId, on: on === true }),
  recordEvent: (dealId, type, date) => call('recordEvent', { dealId, type, date }), // date: YYYY-MM-DD
  submitGate: (taskId) => call('submitGate', { taskId }),
  // RW-1282: deal notes — staff or owning agent; archive is the removal path.
  addNote: (dealId, body) => call('addNote', { dealId, body }),
  updateNote: (noteId, patch) => call('updateNote', { noteId, ...patch }), // { body? | pinned? | archived? }

  // ── gate approval (broker / elevated) ─────────────────────
  approveGate: (taskId) => call('approveGate', { taskId }),

  // ── RW-1837: Ask Rêve's write gate ───────────────────────
  // These live on dealsApi, not aiApi, because the write does — the assistant
  // stages a proposal it cannot execute, and confirming re-enters dealsApi's
  // own createDeal/addNote path with the agent's real permissions. Confirming
  // twice is refused server-side (the approval is spent), so the caller can
  // treat failed-precondition as "already decided", not as an error to retry.
  aiActionConfirm: (actionId) => call('aiActionConfirm', { actionId }),
  aiActionDismiss: (actionId) => call('aiActionDismiss', { actionId }),

  // ── super_admin (template seeding + maintenance) ──────────
  seedTemplates: (force) => call('seedTemplates', force ? { force: true } : {}),
  // RW-1077: the 11-deal Direction C demo book (staging verification).
  seedDemoDeals: (force) => call('seedDemoDeals', force ? { force: true } : {}),
  deleteDeal: (dealId) => call('deleteDeal', { dealId }),
};
